// src/pages/BranchList.jsx
import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import AppConst from "../apis/appconst";
import NewBranch from "../models/NewBranch";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const BranchList = () => {
  const [rows, setRows] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("ldata");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setIsAdmin(parsed?.role_id === 1 || parsed?.role_id === "1");
      } catch (_) {}
    }
    loadBranches();
    // eslint-disable-next-line
  }, []);

  const loadBranches = async () => {
    try {
      const fd = new FormData();
      fd.append("page_no", "none"); // load all
      fd.append("search_key", searchKey || "");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getBranchListUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data && res.data.status) {
        setRows(Array.isArray(res.data.records) ? res.data.records : []);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("loadBranches error", err);
      toast.error("Failed to load branches");
      setRows([]);
    }
  };

  const openAddModal = () => {
    setEditItem(null);
    const el = document.getElementById("addBranchBtnHidden");
    if (el) el.click();
  };

  const handleEdit = (row) => {
    // fetch full branch (with extra fields) then open modal
    setEditItem(row);
    const el = document.getElementById("addBranchBtnHidden");
    if (el) el.click();
  };

  const handleDelete = async (row) => {
    if (!isAdmin) {
      toast.error("Only admin can delete.");
      return;
    }
    const result = await Swal.fire({
      title: `Delete ${row.branch_name || "this branch"}?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append("branch_id", row.branch_id + "");
      const res = await axios.post(AppConst.BASE_URL + AppConst.delBranchUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data && res.data.status) {
        toast.success("Deleted");
        await loadBranches();
      } else {
        toast.error("Delete failed");
      }
    } catch (err) {
      console.error("delete branch error", err);
      toast.error("Delete failed");
    }
  };

  const filteredRows = useMemo(() => {
    const s = (searchKey || "").trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        (r.branch_name || "").toLowerCase().includes(s) ||
        (r.branch_owner_name || "").toLowerCase().includes(s) ||
        (r.branch_contact_no || "").toLowerCase().includes(s) ||
        (r.branch_phone_no || "").toLowerCase().includes(s)
    );
  }, [rows, searchKey]);

  const columns = useMemo(
    () => [
      { name: "Sl. No.", selector: (row, idx) => idx + 1, width: "80px" },
      { name: "Branch Name", selector: (row) => row.branch_name || "", sortable: true, grow: 1 },
      { name: "Contact No", selector: (row) => row.branch_contact_no || "", width: "140px" },
      { name: "Phone No", selector: (row) => row.branch_phone_no || "", width: "140px" },
      { name: "Owner", selector: (row) => row.branch_owner_name || "", sortable: true, width: "160px" },
      { name: "Owner Phone", selector: (row) => row.branch_owner_phone || "", width: "140px" },
      {
        name: "Commission",
        cell: (row) =>
          row.branch_commission_type === "fix"
            ? `₹ ${row.branch_commission || 0}`
            : `${row.branch_commission || 0} %`,
        width: "120px",
      },
      {
        name: "Extra Fields",
        cell: (row) => (row.extra_fields ? row.extra_fields.length : 0),
        width: "110px",
      },
      {
        name: "Action",
        cell: (row) => (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleEdit(row)}
              disabled={!isAdmin}
              title="Edit Branch"
            >
              Edit
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row)}
              disabled={!isAdmin}
              title="Delete Branch"
            >
              Delete
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "180px",
      },
    ],
    // eslint-disable-next-line
    [isAdmin]
  );

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f1f5fb",
        color: "#405189",
        fontSize: "14px",
        fontWeight: "700",
      },
    },
    rows: { style: { minHeight: "56px" } },
    cells: { style: { paddingLeft: "12px", paddingRight: "12px" } },
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card" id="branchList">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Branches</h5>
            <button className="btn btn-success" onClick={openAddModal}>
              <i className="ri-add-line align-bottom me-1" /> Add Branch
            </button>
          </div>

          <div className="card-body border border-dashed border-end-0 border-start-0">
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <input
                  className="form-control"
                  placeholder="Search by name, owner, contact..."
                  type="text"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="card-body pt-0">
            <div className="table-responsive table-card mb-1">
              <DataTable
                columns={columns}
                data={filteredRows}
                customStyles={customStyles}
                noHeader
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50]}
                highlightOnHover
                persistTableHead
                noDataComponent={
                  <div className="p-4 text-muted">No branches yet.</div>
                }
              />
            </div>
          </div>

          {/* Branch modal + hidden trigger */}
          <NewBranch item={editItem} onAddBranch={loadBranches} />
          <button
            id="addBranchBtnHidden"
            style={{ display: "none" }}
            type="button"
            data-bs-toggle="modal"
            data-bs-target="#showBranchModal"
          ></button>
        </div>
      </div>
    </div>
  );
};

export default BranchList;